import { useMemo } from 'react';

import { InventoryProvider } from '@scaleaq/sitecontrol-lib';
import { Control, FieldErrors, Path, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ControlledAutoComplete } from '@/components/inputFields';
import { useClassInventoryProviderPair } from '@/hooks';

import { useSensorsAndProviders } from '../../hooks.ts';
import { AggregatedChartConfigType, LinearChartConfigType } from '../../types.ts';

// Base type containing only the common fields used by this component
type BaseSensorAndTypeConfigType = {
  sensors: { value: string; label: string }[];
  plot: { value: string; label: string }[];
};

// Union type of all config types that use this component
type SensorAndTypeConfigType = LinearChartConfigType | AggregatedChartConfigType;

type MultiSelectSensorAndTypeFieldsProps<
  T extends SensorAndTypeConfigType = SensorAndTypeConfigType,
> = {
  control: Control<T>;
  errors: FieldErrors<T>;
  rootId?: string;
  sensorRequiredErrorKey: string;
};

export const MultiSelectSensorAndTypeFields = <
  T extends SensorAndTypeConfigType = SensorAndTypeConfigType,
>({
  control,
  errors,
  rootId,
  sensorRequiredErrorKey,
}: MultiSelectSensorAndTypeFieldsProps<T>) => {
  const { t } = useTranslation(['chartConfig']);

  // Type assertions needed because Path<T> requires the exact field names
  const sensorsPath = 'sensors' as Path<T>;
  const plotPath = 'plot' as Path<T>;

  const selectedSensors = useWatch({ control, name: sensorsPath });
  const selectedProviderClasses = useWatch({ control, name: plotPath });

  // Type assertions to narrow the types to arrays
  const selectedSensorsArray = selectedSensors as
    | BaseSensorAndTypeConfigType['sensors']
    | undefined;
  const selectedProviderClassesArray = selectedProviderClasses as
    | BaseSensorAndTypeConfigType['plot']
    | undefined;

  const { classInventoryProviderPair } = useClassInventoryProviderPair();

  const selectedProviders = useMemo(
    () =>
      selectedProviderClassesArray
        ?.map(spc => classInventoryProviderPair[spc?.value])
        .filter((provider): provider is InventoryProvider => provider !== undefined) ?? [],
    [selectedProviderClassesArray, classInventoryProviderPair]
  );

  const selectedSensorIds = useMemo(
    () => selectedSensorsArray?.map(s => s?.value),
    [selectedSensorsArray]
  );

  const isMultiSensor = useMemo(
    () => !selectedProviders || selectedProviders.length < 2,
    [selectedProviders]
  );

  const isMultiType = useMemo(
    () => !selectedSensorsArray || selectedSensorsArray.length < 2,
    [selectedSensorsArray]
  );

  const { sensorOptions, providerOptions, isLoading } = useSensorsAndProviders(
    selectedSensorIds,
    selectedProviders,
    rootId
  );

  return (
    <>
      <ControlledAutoComplete
        name={sensorsPath}
        control={control}
        label={t(`chartConfig:chartConfigModal.fields.sensor${isMultiSensor ? 's' : ''}ToPlot`)}
        placeholder={t('chartConfig:placeholder.sensor')}
        options={sensorOptions}
        rules={{
          required: sensorRequiredErrorKey,
        }}
        caution={errors.sensors?.message as string | boolean | undefined}
        isLoading={isLoading}
        isMulti={isMultiSensor}
        isClearable={true}
      />
      <ControlledAutoComplete
        render={!!selectedSensorsArray?.length || !!selectedProviders?.length}
        control={control}
        options={providerOptions}
        label={t(`chartConfig:chartConfigModal.fields.type${isMultiType ? 's' : ''}ToPlot`)}
        placeholder={t('chartConfig:placeholder.type')}
        name={plotPath}
        isLoading={isLoading}
        isMulti={isMultiType}
        rules={{ required: t('chartConfig:chartConfigModal.rules.isRequired.type') }}
        caution={errors.plot?.message as string | boolean | undefined}
        isClearable={true}
      />
    </>
  );
};
